import { createRouter, createWebHistory, LocationQuery, RouteLocationNormalized, RouteRecordRaw } from 'vue-router'
import AppLayout from '../layouts/AppLayout.vue'
import EducationLayout from '../layouts/EducationLayout.vue'
import i18n from '../i18n'
import {
  isPrefixedLocale,
  localeFromPath,
  normalizeAppLocale,
  pathForLocale,
  applyDocumentDirection,
  type AppLocale,
} from '../utils/locale'
import { resolveNewsSlug } from '../utils/newsList'
import {
  EDUCATION_SITE_URL,
  MAIN_SITE_URL,
  educationPublicPath,
  isEducationHost,
  shouldRedirectEducationToSubdomain,
} from '../utils/educationHost'

function isEducationPath(path: string) {
  return path === '/education' || path.startsWith('/education/')
}

function queryString(query: LocationQuery): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item != null) params.append(key, String(item))
      }
    } else if (value != null) {
      params.set(key, String(value))
    }
  }
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

function absoluteSiteUrl(origin: string, pathname: string, to: Pick<RouteLocationNormalized, 'query' | 'hash'>) {
  const path = pathname.startsWith('/') ? pathname : `/${pathname}`
  return `${origin}${path}${queryString(to.query)}${to.hash || ''}`
}

const educationChildren: RouteRecordRaw[] = [
  {
    name: 'education-home',
    path: '',
    component: () => import('../pages/education/EducationHomePage.vue'),
  },
  {
    name: 'education-programs',
    path: 'programs',
    component: () => import('../pages/education/EducationProgramsPage.vue'),
  },
  {
    name: 'education-program-detail',
    path: 'programs/:slug',
    component: () => import('../pages/education/EducationProgramDetailPage.vue'),
  },
  {
    name: 'education-university',
    path: 'university',
    component: () => import('../pages/education/EducationUniversityPage.vue'),
  },
  {
    name: 'education-how-it-works',
    path: 'how-it-works',
    component: () => import('../pages/education/EducationHowItWorksPage.vue'),
  },
  {
    name: 'education-faq',
    path: 'faq',
    component: () => import('../pages/education/EducationFaqPage.vue'),
  },
  {
    name: 'education-contact',
    path: 'contact',
    component: () => import('../pages/education/EducationContactPage.vue'),
  },
]

const mainSiteRoutes: Array<RouteRecordRaw> = [
  {
    path: '/:pathMatch(.*)*',
    redirect: (to) => {
      const localeParam = typeof to.params.locale === 'string' ? to.params.locale : undefined
      const locale = isPrefixedLocale(localeParam) ? localeParam : undefined
      return {
        name: 'home',
        params: locale ? { locale } : {},
      }
    },
  },
  {
    name: 'education-root',
    path: '/education',
    component: EducationLayout,
    children: educationChildren,
  },
  {
    name: 'root',
    path: '/:locale(cn|ru|ar|ko)?',
    component: AppLayout,
    children: [
      {
        name: 'home',
        path: '',
        alias: 'home',
        component: () => import('../pages/HomePage.vue'),
      },
      {
        name: 'contact-us',
        path: 'contact-us',
        component: () => import('../pages/ContactPage.vue'),
      },
      {
        name: 'about-us',
        path: 'about-us',
        component: () => import('../pages/AboutPage.vue'),
      },
      {
        name: 'partnership',
        path: 'partnership',
        component: () => import('../pages/PartnershipPage.vue'),
      },
      {
        name: 'programs',
        path: 'programs',
        component: () => import('../pages/ProgramsPage.vue'),
      },
      {
        name: 'program',
        path: 'program/:country',
        component: () => import('../pages/ProgramPage.vue'),
      },
      {
        name: 'news',
        path: 'insights',
        component: () => import('../pages/NewsPage.vue'),
      },
      {
        name: 'news-detail',
        path: 'insights/:slug',
        component: () => import('../pages/NewsDetailPage.vue'),
      },
      {
        path: 'news',
        redirect: (to) => ({
          name: 'news',
          params: typeof to.params.locale === 'string' ? { locale: to.params.locale } : {},
        }),
      },
      {
        path: 'news/:slug',
        redirect: (to) => ({
          name: 'news-detail',
          params: {
            ...(typeof to.params.locale === 'string' ? { locale: to.params.locale } : {}),
            slug: String(to.params.slug),
          },
        }),
      },
      {
        path: 'news-detail/:id',
        redirect: (to) => ({
          name: 'news-detail',
          params: {
            ...(typeof to.params.locale === 'string' ? { locale: to.params.locale } : {}),
            slug: resolveNewsSlug(String(to.params.id)),
          },
        }),
      },
      {
        name: 'legal-notice',
        path: 'legal-notice',
        component: () => import('../pages/LegalDocumentPage.vue'),
      },
      {
        name: 'privacy-policy',
        path: 'privacy-policy',
        component: () => import('../pages/LegalDocumentPage.vue'),
      },
      {
        name: 'terms-of-use',
        path: 'terms-of-use',
        component: () => import('../pages/LegalDocumentPage.vue'),
      },
      // Education lives at /education (no locale prefix); redirect prefixed URLs.
      {
        path: 'education/:educationPath(.*)*',
        redirect: (to) => {
          const rest = to.params.educationPath
          const suffix = Array.isArray(rest) ? rest.filter(Boolean).join('/') : rest || ''
          return suffix ? `/education/${suffix}` : '/education'
        },
      },
    ],
  },
]

const legalDocumentRoutes: RouteRecordRaw[] = [
  {
    name: 'legal-notice',
    path: 'legal-notice',
    component: () => import('../pages/LegalDocumentPage.vue'),
  },
  {
    name: 'privacy-policy',
    path: 'privacy-policy',
    component: () => import('../pages/LegalDocumentPage.vue'),
  },
  {
    name: 'terms-of-use',
    path: 'terms-of-use',
    component: () => import('../pages/LegalDocumentPage.vue'),
  },
]

const educationHostRoutes: Array<RouteRecordRaw> = [
  {
    name: 'education-root',
    path: '/',
    component: EducationLayout,
    children: [...educationChildren, ...legalDocumentRoutes],
  },
  {
    path: '/:pathMatch(.*)*',
    component: { render: () => null },
    beforeEnter(to) {
      window.location.replace(`${MAIN_SITE_URL}${to.fullPath}`)
      return false
    },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes: isEducationHost() ? educationHostRoutes : mainSiteRoutes,
  strict: false,
})

router.beforeEach((to) => {
  if (isEducationHost()) {
    const publicPath = educationPublicPath(to.path)
    if (publicPath) {
      return { path: publicPath, query: to.query, hash: to.hash, replace: true }
    }

    const onEducationRoute = to.matched.some((record) => String(record.name || '').startsWith('education'))
    if (!onEducationRoute) {
      window.location.replace(`${MAIN_SITE_URL}${to.fullPath}`)
      return false
    }

    i18n.global.locale.value = 'cn'
    applyDocumentDirection('cn')
    return true
  }

  if (shouldRedirectEducationToSubdomain()) {
    const publicPath = educationPublicPath(to.path)
    if (publicPath) {
      window.location.replace(absoluteSiteUrl(EDUCATION_SITE_URL, publicPath, to))
      return false
    }
  }

  // Education section is Chinese-only and never uses locale URL prefixes.
  if (isEducationPath(to.path) || String(to.name || '').startsWith('education')) {
    i18n.global.locale.value = 'cn'
    applyDocumentDirection('cn')
    return true
  }

  const localeFromRoute = isPrefixedLocale(to.params.locale) ? (to.params.locale as AppLocale) : localeFromPath(to.path)
  const currentLocale = normalizeAppLocale(i18n.global.locale.value)

  if (isPrefixedLocale(to.params.locale)) {
    i18n.global.locale.value = localeFromRoute
    applyDocumentDirection(localeFromRoute)
    return true
  }

  // Keep non-default locales explicitly visible in URL.
  if (isPrefixedLocale(currentLocale)) {
    if (to.name) {
      return {
        name: to.name,
        params: { ...to.params, locale: currentLocale },
        query: to.query,
        hash: to.hash,
      }
    }

    return {
      path: pathForLocale(to.path, currentLocale),
      query: to.query,
      hash: to.hash,
    }
  }

  i18n.global.locale.value = 'gb'
  applyDocumentDirection('gb')
  return true
})

export default router
